import toast from "react-hot-toast";
import {
  Funnel,
  Plus,
  Edit2,
  Trash2,
  Search,
  Layers,
  CircleHelp,
  RefreshCw,
  TriangleAlert,
  Users,
} from "lucide-react";
import { useState } from "react";
import ConfirmModal from "../modal/ConfirmModal";
import FunnelModal from "../modal/FunnelModal";
import { useFunnels } from "../hooks/useFunnels";
import { useFunnelPipeline } from "../hooks/useFunnelPipeline";
import { useConversationsApi } from "../api/conversationsApi";
import PipelineBoard from "../components/funnel/PipelineBoard";

const STALLED_OPTIONS = [1, 3, 7, 15];

const StatCard = ({ icon: Icon, tone, value, label, hint, alert }) => (
  <div
    className={`rounded-lg border bg-white p-4 shadow-sm transition-colors dark:bg-gray-900 ${
      alert ? "border-amber-300 dark:border-amber-800" : "border-gray-200 dark:border-gray-700"
    }`}
  >
    <div className="mb-1 flex items-center justify-between">
      <Icon className={`h-5 w-5 ${tone}`} />
      <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</span>
    </div>
    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
    {hint && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
  </div>
);

const FunnelManager = ({ onOpenConversation }) => {
  const { funnels, addFunnel, updateFunnel, deleteFunnel, loading, error } =
    useFunnels();

  const [tab, setTab] = useState("pipeline");
  const [funnelId, setFunnelId] = useState(null);
  const [stalledDays, setStalledDays] = useState(3);
  const [movingId, setMovingId] = useState(null);
  const conversationsApi = useConversationsApi();
  const {
    pipeline,
    loading: loadingPipeline,
    refetch,
  } = useFunnelPipeline({ funnelId, stalledDays });

  const handleMove = async (conversationId, step) => {
    setMovingId(conversationId);
    try {
      await conversationsApi.update(conversationId, {
        funnel: step === "__none__" ? null : step,
      });
      await refetch();
      toast.success(
        step === "__none__" ? "Conversa saiu do funil." : `Movida para ${step}.`,
      );
    } catch (err) {
      toast.error(err.message || "Não foi possível mover a conversa.");
    } finally {
      setMovingId(null);
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [funnelToDelete, setFunnelToDelete] = useState(null);

  const filteredFunnels = funnels.filter((funnel) => {
    const term = searchTerm.toLowerCase();
    const nameMatch = funnel.name?.toLowerCase().includes(term);
    const stepsMatch = funnel.steps
      ?.map((s) => s.toLowerCase())
      .join(" ")
      .includes(term);
    return nameMatch || stepsMatch;
  });

  const openFunnelModal = () => {
    setSelectedFunnel(null);
    setIsModalOpen(true);
  };

  const handleSaveFunnel = async (funnelData) => {
    try {
      if (selectedFunnel) {
        await updateFunnel(selectedFunnel.id, funnelData);
        toast.success("Funil atualizado com sucesso!");
      } else {
        await addFunnel(funnelData);
        toast.success("Funil criado com sucesso!");
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Ocorreu um erro ao salvar o funil.");
    }
  };

  const handleEditFunnel = (funnel) => {
    setSelectedFunnel(funnel);
    setIsModalOpen(true);
  };

  const handleDeleteFunnel = (funnelId) => {
    setFunnelToDelete(funnelId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (funnelToDelete) {
        await deleteFunnel(funnelToDelete);
        toast.success("Funil excluído com sucesso!");
        setFunnelToDelete(null);
        setConfirmOpen(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao excluir o funil.");
    }
  };

  if (loading)
    return (
      <p className="p-6 text-gray-700 dark:text-gray-300">
        Carregando funis...
      </p>
    );
  if (error) return <p className="text-red-500">Erro: {error}</p>;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6 transition-colors duration-300 dark:bg-gray-800">
      <div className="mx-auto max-w-7xl">
        {/* Cabeçalho */}
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Gestão de Funis
          </h1>
          <button
            onClick={openFunnelModal}
            className="flex items-center rounded-lg bg-green-500 px-4 py-2 text-white transition-colors hover:bg-green-600"
          >
            <Plus className="mr-2 h-4 w-4" />
            Novo Funil
          </button>
        </div>

        {/* Indicadores do pipeline */}
        {pipeline && (
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              icon={Funnel}
              tone="text-green-500"
              value={pipeline.summary.inFunnel}
              label="No funil"
              hint={`${pipeline.stages.length} etapas ativas`}
            />
            <StatCard
              icon={TriangleAlert}
              tone="text-amber-500"
              value={pipeline.summary.stalled}
              label={`Paradas há ${pipeline.stalledDays}+ dias`}
              hint="Sem nenhuma mensagem no período"
              alert={pipeline.summary.stalled > 0}
            />
            <StatCard
              icon={CircleHelp}
              tone="text-blue-500"
              value={pipeline.summary.withoutStage}
              label="Sem etapa"
              hint="Conversas fora do funil"
            />
            <StatCard
              icon={Users}
              tone="text-purple-500"
              value={pipeline.summary.bottleneck || "—"}
              label="Maior gargalo"
              hint="Etapa com mais conversas paradas"
            />
          </div>
        )}

        {/* Abas */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-gray-300 p-0.5 dark:border-gray-600">
            {[
              { value: "pipeline", label: "Pipeline" },
              { value: "steps", label: "Etapas" },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setTab(option.value)}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  tab === option.value
                    ? "bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900"
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {tab === "pipeline" && pipeline && (
            <>
              <select
                value={pipeline.selectedFunnelId || ""}
                onChange={(event) => setFunnelId(event.target.value)}
                className="rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                aria-label="Funil"
              >
                {pipeline.funnels.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>

              <select
                value={stalledDays}
                onChange={(event) => setStalledDays(Number(event.target.value))}
                className="rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                aria-label="Considerar parada após"
              >
                {STALLED_OPTIONS.map((days) => (
                  <option key={days} value={days}>
                    Parada após {days} {days === 1 ? "dia" : "dias"}
                  </option>
                ))}
              </select>

              <button
                onClick={refetch}
                disabled={loadingPipeline}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <RefreshCw className={`h-4 w-4 ${loadingPipeline ? "animate-spin" : ""}`} />
                Atualizar
              </button>
            </>
          )}
        </div>

        {tab === "pipeline" && (
          <>
            {!pipeline ? (
              <p className="text-gray-600 dark:text-gray-400">Carregando pipeline...</p>
            ) : pipeline.funnels.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
                <Layers className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                <p className="text-gray-600 dark:text-gray-400">
                  Crie um funil e suas etapas para acompanhar o pipeline aqui.
                </p>
              </div>
            ) : (
              <>
                <PipelineBoard
                  pipeline={pipeline}
                  busyId={movingId}
                  onOpen={onOpenConversation}
                  onMove={handleMove}
                />

                {pipeline.summary.truncated && (
                  <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">
                    O volume de conversas excedeu o limite de varredura; os números
                    cobrem apenas as mais recentes.
                  </p>
                )}
              </>
            )}
          </>
        )}

        {/* Tabela de Funis */}
        <div
          className={`overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-colors duration-300 dark:border-gray-700 dark:bg-gray-900 ${
            tab === "steps" ? "" : "hidden"
          }`}
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Lista de Funis
            </h2>

            <div className="relative w-64">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Etapas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Criado em
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Atualizado em
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Ações
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                {filteredFunnels.map((funnel) => (
                  <tr
                    key={funnel.id}
                    className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {funnel.name}
                      </span>
                    </td>

                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {funnel.steps && funnel.steps.length > 0
                        ? funnel.steps.join(" → ")
                        : "Nenhuma etapa"}
                    </td>

                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {funnel.createdAt
                        ? new Date(funnel.createdAt).toLocaleDateString()
                        : "-"}
                    </td>

                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {funnel.updatedAt
                        ? new Date(funnel.updatedAt).toLocaleDateString()
                        : "-"}
                    </td>

                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditFunnel(funnel)}
                          className="rounded p-1 text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                          title="Editar"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteFunnel(funnel.id)}
                          className="rounded p-1 text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {funnels.length === 0 && (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      Nenhum funil encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modais */}
      <FunnelModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveFunnel}
        funnel={selectedFunnel}
      />

      <ConfirmModal
        isOpen={confirmOpen}
        title="Excluir Funil"
        message="Tem certeza que deseja excluir este funil? Esta ação não pode ser desfeita."
        onConfirm={confirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default FunnelManager;
